import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import {
	ClientsService,
	type IGetClientsParams,
} from '@services/clients.service';

// --- Stores ---
import { ClientsStore, type IClientsState } from '@stores/clients.store';

// --- Utils ---
import { simpleDiff } from '@utils/simple-diff.util';

// --- Interfaces ---
import type { IClient } from '@interfaces/client';

const REFRESH_INTERVAL = 120 * 1000;

@Injectable({
	providedIn: 'root',
})
export class ClientsFacade {
	state!: IClientsState;

	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);
	readonly refresh$ = this._refresh.asObservable();

	private readonly _filter = new BehaviorSubject<IGetClientsParams>({});
	readonly filter$ = this._filter.asObservable().pipe(
		debounceTime(300),
		distinctUntilChanged((prev, curr) => !simpleDiff(prev, curr))
	);

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.refresh$,
		this.autoRefresh$,
	]).pipe(switchMap(([filter]) => this.getClients(filter)));

	readonly clientsState$ = combineLatest([
		this.clientsStore.clientsState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => state),
		tap(state => (this.state = state)),
		shareReplay({ refCount: true })
	);

	constructor(
		private clientsService: ClientsService,
		private clientsStore: ClientsStore
	) {}

	getClients(queryParams?: IGetClientsParams) {
		return this.clientsService
			.getClients(queryParams)
			.pipe(tap(clients => this.clientsStore.updateClients(clients)));
	}

	getClientById(id: IClient['id']) {
		return this.clientsService.getClientById(id);
	}

	lookupCnpj(document: string) {
		return this.clientsService.lookupCnpj(document);
	}

	newClient(client: IClient) {
		return this.clientsService
			.postClient(client)
			.pipe(tap(client => this.clientsStore.updateClient(client)));
	}

	updateClient(id: IClient['id'], client: IClient) {
		return this.clientsService
			.updateClient(id, client)
			.pipe(tap(client => this.clientsStore.updateClient(client)));
	}

	activateClient(id: IClient['id']) {
		return this.clientsService.activateClient(id);
	}

	inactivateClient(id: IClient['id']) {
		return this.clientsService.inactivateClient(id);
	}

	filterClients(filter: IGetClientsParams) {
		this._filter.next(filter);
	}
}
